import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, MoreHorizontal, CircleCheck, Ban, ClipboardList } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage, apiErrorType } from "@/lib/api-error";
import { formatCurrency, formatDate, formatQuantity } from "@/lib/format";
import { UnitCombobox } from "@/components/UnitCombobox";
import { Field, FormSection, LineCard, RequiredLegend } from "@/components/DocForm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { DocStatusBadge } from "@/components/PoDnStatus";
import {
  EmptyState,
  EntityCell,
  ErrorState,
  HeaderRow,
  LoadingState,
  Pager,
  TH,
  TableShell,
  rowLink,
} from "@/components/PageUi";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import type { components } from "@dolfin/api-public";

type PurchaseOrderResponse = components["schemas"]["PurchaseOrderResponse"];
type SupplierResponse = components["schemas"]["SupplierResponse"];
// Body accepted by PUT /purchase-orders/by-number/{poNumber} (the re-upload / replace path).
type PoWriteBody = components["schemas"]["UpdatePurchaseOrderRequest"];

interface LineDraft {
  // Present for an existing PO line (so the server reconciles by identity); absent for a newly added line.
  id?: string;
  // The caller's own back-pointer: never identifies the line (that's `id`) or affects matching.
  externalRef: string;
  description: string;
  sku: string;
  quantityOrdered: string;
  unit: string;
  unitPrice: string;
}

const emptyLine = (): LineDraft => ({
  externalRef: "",
  description: "",
  sku: "",
  quantityOrdered: "1",
  unit: "",
  unitPrice: "0",
});

// Untouched rows are dropped on submit; a row with any content must validate, so nothing typed is discarded.
const isMeaningful = (l: LineDraft) =>
  Boolean(l.description.trim() || l.sku.trim() || l.externalRef.trim());

interface LineErrors {
  description: string | null;
  quantityOrdered: string | null;
}

const hasLineError = (e: LineErrors) => e.description !== null || e.quantityOrdered !== null;

// Mirrors CreatePurchaseOrderRequestValidator: PO number, a 3-letter currency, at least one line,
// and every line needs a description and a quantity above zero.
function validate(poNumber: string, currency: string, lines: LineDraft[]) {
  const kept = lines.filter(isMeaningful);
  return {
    poNumber: poNumber.trim() ? null : "A purchase order needs a PO number.",
    currency: currency.trim().length === 3 ? null : "Must be a 3-letter ISO code.",
    noLines: kept.length === 0 ? "Add at least one line." : null,
    lines: lines.map<LineErrors>((l) => {
      if (!isMeaningful(l)) return { description: null, quantityOrdered: null };
      return {
        description: l.description.trim() ? null : "Required.",
        quantityOrdered: Number(l.quantityOrdered) > 0 ? null : "Must be above 0.",
      };
    }),
  };
}

// The API carries these as full date-time ISO strings; <input type="date"> wants YYYY-MM-DD.
const toDateInput = (iso: string | null | undefined) => (iso ? iso.slice(0, 10) : "");
const toDateTimeBody = (value: string) => (value ? `${value}T00:00:00Z` : null);

const PAGE_SIZE = 20;

// Statuses that the backend refuses to edit (PurchaseOrder.NotEditable).
const TERMINAL_STATUSES = new Set(["Closed", "Cancelled"]);
const isEditable = (po: PurchaseOrderResponse) => !TERMINAL_STATUSES.has(String(po.status));

export interface PurchaseOrdersTableProps {
  /** When set, only purchase orders for this supplier are listed. */
  supplierId?: string;
  onSelect?: (purchaseOrderId: string) => void;
  /** Bump this number to force a refetch (e.g. after the host creates a PO). */
  reloadSignal?: number;
  /** Reports the number of purchase orders currently shown, so the host can render a count. */
  onCountChange?: (count: number) => void;
}

export function PurchaseOrdersTable({
  supplierId,
  onSelect,
  reloadSignal,
  onCountChange,
}: PurchaseOrdersTableProps) {
  const api = useDolfinClient();
  const [orders, setOrders] = useState<PurchaseOrderResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<PurchaseOrderResponse | null>(null);
  const [acting, setActing] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  // Scoped to one supplier, the counterparty column would only repeat itself.
  const showSupplier = !supplierId;
  const supplierNames = useSupplierNames(showSupplier);

  const fetchOrders = useCallback(
    async (cursor?: string) => {
      setLoading(true);
      setError(null);
      const query: Record<string, string | number> = { Limit: PAGE_SIZE };
      if (supplierId) query.SupplierId = supplierId;
      if (cursor) query.Cursor = cursor;
      const { data, error: apiError } = await api.GET("/v1/purchase-orders", {
        params: { query },
      });
      if (apiError || !data) {
        setError(apiErrorMessage(apiError, "Couldn't load purchase orders."));
      } else {
        const rows = data.data ?? [];
        setOrders(rows);
        onCountChange?.(rows.length);
        const next = data.meta?.cursors?.next ?? null;
        setNextCursor(next);
        setHasMore(next !== null);
      }
      setLoading(false);
    },
    [api, supplierId, onCountChange],
  );

  useEffect(() => {
    setCursorStack([]);
    setNextCursor(null);
    fetchOrders();
    // reloadSignal is an explicit refetch trigger from the host.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchOrders, reloadSignal]);

  const currentPage = cursorStack.length + 1;
  // Cursor that loaded the page currently in view (undefined on page 1); used to
  // refetch in place after an edit / close / cancel without jumping to page 1.
  const currentCursor = cursorStack[cursorStack.length - 1];

  function handleNextPage() {
    if (!nextCursor) return;
    setCursorStack((prev) => [...prev, nextCursor]);
    fetchOrders(nextCursor);
  }

  function handlePrevPage() {
    const newStack = [...cursorStack];
    newStack.pop();
    const prevCursor = newStack[newStack.length - 1];
    setCursorStack(newStack);
    fetchOrders(prevCursor);
  }

  async function transition(po: PurchaseOrderResponse, action: "close" | "cancel") {
    setActing(true);
    const { error: apiError } =
      action === "close"
        ? await api.POST("/v1/purchase-orders/{id}/close", { params: { path: { id: po.id! } } })
        : await api.POST("/v1/purchase-orders/{id}/cancel", { params: { path: { id: po.id! } } });
    if (apiError) {
      toast.error(apiErrorMessage(apiError, `Couldn't ${action} the purchase order.`));
    } else {
      toast.success(`Purchase order ${action === "close" ? "closed" : "cancelled"}.`);
      await fetchOrders(currentCursor);
    }
    setActing(false);
  }

  if (error) {
    return <ErrorState message={error} onRetry={() => fetchOrders(currentCursor)} />;
  }

  if (loading) {
    return <LoadingState label="Loading purchase orders..." />;
  }

  if (orders.length === 0) {
    return (
      <EmptyState icon={ClipboardList} title={supplierId ? "No purchase orders for this supplier" : "No purchase orders yet"}>
        Raise a purchase order and Dolfin matches the supplier&apos;s bills against it as they arrive.
      </EmptyState>
    );
  }

  return (
    <>
      <TableShell>
        <Table>
          <TableHeader>
            <HeaderRow>
              <TableHead className={TH}>{showSupplier ? "Supplier" : "Purchase order"}</TableHead>
              {showSupplier ? <TableHead className={TH}>PO number</TableHead> : null}
              <TableHead className={TH}>Status</TableHead>
              <TableHead className={TH}>Lines</TableHead>
              <TableHead className={TH}>Expected</TableHead>
              <TableHead className={cn(TH, "text-right")}>Net total</TableHead>
              <TableHead className="w-10"><span className="sr-only">Edit</span></TableHead>
              <TableHead className="w-10"><span className="sr-only">More actions</span></TableHead>
            </HeaderRow>
          </TableHeader>
          <TableBody>
            {orders.map((po) => {
              const net = (po.lines ?? []).reduce(
                (sum, l) => sum + Number(l.subTotal ?? 0),
                0,
              );
              const raised = `Raised ${formatDate(po.orderDate ?? po.createdAt)}`;
              return (
                <TableRow key={po.id} {...rowLink(onSelect ? () => onSelect(po.id!) : undefined)}>
                  <TableCell className="max-w-[280px]">
                    {showSupplier ? (
                      <EntityCell
                        name={supplierLabel(po.supplierId, supplierNames)}
                        fallback="No supplier yet"
                        secondary={raised}
                      />
                    ) : (
                      <div className="min-w-0">
                        <div className="truncate font-mono text-sm font-semibold">{po.poNumber || "(no number)"}</div>
                        <div className="truncate text-xs text-muted-foreground">{raised}</div>
                      </div>
                    )}
                  </TableCell>
                  {showSupplier ? (
                    <TableCell className="font-mono text-xs whitespace-nowrap">{po.poNumber || "(no number)"}</TableCell>
                  ) : null}
                  <TableCell>
                    <DocStatusBadge status={po.status} />
                  </TableCell>
                  <TableCell className="max-w-72 whitespace-normal"><LineChips lines={po.lines ?? []} /></TableCell>
                  <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                    {po.expectedDeliveryDate ? formatDate(po.expectedDeliveryDate) : "-"}
                  </TableCell>
                  <TableCell className="text-right font-semibold whitespace-nowrap tabular-nums">
                    {formatCurrency(net, po.currency)}
                  </TableCell>
                  <TableCell className="text-right" onClick={stop} onKeyDown={stop}>
                    {isEditable(po) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Edit purchase order"
                        onClick={() => setEditing(po)}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    )}
                  </TableCell>
                  <TableCell className="text-right" onClick={stop} onKeyDown={stop}>
                    {isEditable(po) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="More actions" disabled={acting}>
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => transition(po, "close")}>
                            <CircleCheck className="size-4" /> Close
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => transition(po, "cancel")}
                          >
                            <Ban className="size-4" /> Cancel
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableShell>

      <Pager
        page={currentPage}
        hasPrevious={currentPage > 1}
        hasNext={hasMore}
        onPrevious={handlePrevPage}
        onNext={handleNextPage}
      />

      <PurchaseOrderDialog
        open={editing !== null}
        editing={editing}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        onSaved={() => fetchOrders(currentCursor)}
      />
    </>
  );
}

// Clicks and Enter inside the action cells belong to the buttons, not to the row link around them.
const stop = (e: React.SyntheticEvent) => e.stopPropagation();

const SUPPLIER_NAME_LIMIT = 100;

/** The purchase order carries only the supplier id; a list of guids says nothing about who it is with. */
export function useSupplierNames(enabled = true) {
  const api = useDolfinClient();
  const [names, setNames] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    api
      .GET("/v1/suppliers", { params: { query: { Limit: SUPPLIER_NAME_LIMIT } } })
      .then(({ data }) => {
        if (cancelled) return;
        setNames(new Map((data?.data ?? []).map((s) => [s.id!, s.name])));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [api, enabled]);
  return names;
}

/** A supplier's display name, or null when none is assigned (the cell then says so). */
export function supplierLabel(id: string | null | undefined, names: Map<string, string>) {
  if (!id) return null;
  return names.get(id) ?? "Supplier";
}

type PurchaseOrderLine = NonNullable<PurchaseOrderResponse["lines"]>[number];

const MAX_VISIBLE_CHIPS = 3;
const MAX_DESCRIPTION_CHARS = 30;

function LineChips({ lines }: { lines: PurchaseOrderLine[] }) {
  const [expanded, setExpanded] = useState(false);

  if (lines.length === 0) {
    return <span className="text-muted-foreground">No lines</span>;
  }

  const visible = expanded ? lines : lines.slice(0, MAX_VISIBLE_CHIPS);
  const hidden = lines.length - visible.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((line, i) => {
        const description = line.description ?? "";
        // Collapsed chips are trimmed to keep rows short; expanding shows every line in full.
        const shown =
          !expanded && description.length > MAX_DESCRIPTION_CHARS
            ? `${description.slice(0, MAX_DESCRIPTION_CHARS).trimEnd()}...`
            : description;
        return (
          <Badge key={line.id ?? i} variant="secondary" className="font-normal">
            {formatQuantity(line.quantityOrdered, line.unit, line.unitOfMeasure)} x {shown || "(no description)"}
          </Badge>
        );
      })}
      {hidden > 0 && (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-2"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(true);
          }}
          onKeyDown={stop}
        >
          +{hidden} more
        </button>
      )}
      {expanded && (
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground text-xs underline underline-offset-2"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(false);
          }}
          onKeyDown={stop}
        >
          Show less
        </button>
      )}
    </div>
  );
}

export function PurchaseOrderDialog({
  open,
  editing,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  editing: PurchaseOrderResponse | null;
  onOpenChange: (open: boolean) => void;
  onSaved: (po: PurchaseOrderResponse) => void;
}) {
  const api = useDolfinClient();
  const [suppliers, setSuppliers] = useState<SupplierResponse[]>([]);
  const [supplierId, setSupplierId] = useState<string>("");
  const [poNumber, setPoNumber] = useState("");
  const [currency, setCurrency] = useState("GBP");
  const [externalRef, setExternalRef] = useState("");
  const [orderDate, setOrderDate] = useState("");
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // When a create hits a duplicate PO number (409), we stash the body here to offer
  // an explicit "replace its lines" via PUT /purchase-orders/by-number/{poNumber}.
  const [duplicate, setDuplicate] = useState<{ poNumber: string; body: PoWriteBody } | null>(null);
  const [replacing, setReplacing] = useState(false);
  // Validation is derived from the current values on every render but only *shown* once the user has
  // tried to submit, so an untouched blank form isn't already red.
  const [showErrors, setShowErrors] = useState(false);

  const isEdit = editing !== null;

  const kept = lines.filter(isMeaningful);
  const fieldErrors = validate(poNumber, currency, lines);
  const hasErrors =
    fieldErrors.poNumber !== null ||
    fieldErrors.currency !== null ||
    fieldErrors.noLines !== null ||
    fieldErrors.lines.some(hasLineError);

  // Keyed on the PO id so reopening for a different row reloads cleanly.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setShowErrors(false);
    if (editing) {
      setSupplierId(editing.supplierId ?? "");
      setPoNumber(editing.poNumber ?? "");
      setCurrency(editing.currency ?? "GBP");
      setExternalRef(editing.externalRef ?? "");
      setOrderDate(toDateInput(editing.orderDate));
      setExpectedDeliveryDate(toDateInput(editing.expectedDeliveryDate));
      const drafts = [...(editing.lines ?? [])]
        .sort((a, b) => Number(a.sortOrder ?? 0) - Number(b.sortOrder ?? 0))
        .map((l) => ({
          id: l.id,
          externalRef: l.externalRef ?? "",
          description: l.description ?? "",
          sku: l.sku ?? "",
          quantityOrdered: String(l.quantityOrdered ?? "0"),
          unit: l.unit ?? "",
          unitPrice: String(l.unitPrice ?? "0"),
        }));
      setLines(drafts.length > 0 ? drafts : [emptyLine()]);
    } else {
      setSupplierId("");
      setPoNumber("");
      setCurrency("GBP");
      setExternalRef("");
      setOrderDate("");
      setExpectedDeliveryDate("");
      setLines([emptyLine()]);
    }
    api
      .GET("/v1/suppliers", { params: { query: { Limit: 100 } } })
      .then(({ data }) => setSuppliers(data?.data ?? []))
      .catch(() => setSuppliers([]));
  }, [open, editing, api]);

  function updateLine(index: number, patch: Partial<LineDraft>) {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setShowErrors(true);
    if (hasErrors) return;

    const cleanLines = kept;
    const lineBody = cleanLines.map((l, i) => {
      const qty = Number(l.quantityOrdered) || 0;
      const price = Number(l.unitPrice) || 0;
      return {
        externalRef: l.externalRef.trim() || null,
        description: l.description.trim(),
        sku: l.sku.trim() || null,
        quantityOrdered: qty,
        unit: l.unit.trim() || null,
        unitPrice: price,
        taxRate: 0,
        taxAmount: 0,
        subTotal: Number((qty * price).toFixed(2)),
        sortOrder: i,
      };
    });

    const header = {
      supplierId: supplierId || null,
      poNumber: poNumber.trim() || null,
      currency,
      externalRef: externalRef.trim() || null,
      orderDate: toDateTimeBody(orderDate),
      expectedDeliveryDate: toDateTimeBody(expectedDeliveryDate),
    };

    setSubmitting(true);
    const { data, error: apiError } =
      isEdit && editing
        ? await api.PUT("/v1/purchase-orders/{id}", {
            params: { path: { id: editing.id! } },
            body: {
              ...header,
              // Carry each existing line's id so the server updates in place / deletes omitted lines;
              // new lines have no id and are inserted.
              lines: lineBody.map((l, i) => ({ id: cleanLines[i]?.id ?? null, ...l })),
            },
          })
        : await api.POST("/v1/purchase-orders", {
            body: {
              ...header,
              lines: lineBody,
            },
          });

    if (apiError || !data) {
      // Creating a PO whose number already exists: instead of a dead-end error,
      // offer to replace the existing PO's lines by number (the idempotent re-upload path).
      const trimmedPo = poNumber.trim();
      if (
        !isEdit &&
        trimmedPo &&
        apiErrorType(apiError).includes("PurchaseOrder.DuplicateNumber")
      ) {
        setDuplicate({
          poNumber: trimmedPo,
          body: { ...header, poNumber: trimmedPo, lines: lineBody },
        });
        setSubmitting(false);
        return;
      }
      const message = apiErrorMessage(
        apiError,
        isEdit ? "Failed to update purchase order." : "Failed to create purchase order.",
      );
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    toast.success(isEdit ? "Purchase order updated" : "Purchase order created");
    onSaved(data);
    setSubmitting(false);
    onOpenChange(false);
  }

  const netTotal = kept.reduce(
    (sum, l) => sum + (Number(l.quantityOrdered) || 0) * (Number(l.unitPrice) || 0),
    0,
  );

  async function handleReplace() {
    if (!duplicate || replacing) return;
    setReplacing(true);
    const { data, error: apiError } = await api.PUT("/v1/purchase-orders/by-number/{poNumber}", {
      params: { path: { poNumber: duplicate.poNumber } },
      body: duplicate.body,
    });
    if (apiError || !data) {
      toast.error(apiErrorMessage(apiError, "Failed to replace purchase order."));
      setReplacing(false);
      return;
    }
    toast.success(`Purchase order ${duplicate.poNumber} replaced`);
    setReplacing(false);
    setDuplicate(null);
    onSaved(data);
    onOpenChange(false);
  }

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-0 p-0 sm:max-w-5xl">
        <form onSubmit={handleSubmit} className="flex max-h-[88vh] flex-col">
          <DialogHeader className="shrink-0 border-b px-6 py-4 pr-12">
            <DialogTitle>{isEdit ? "Edit purchase order" : "Create purchase order"}</DialogTitle>
            <DialogDescription>
              Raise the order, then Dolfin three-way matches it against the supplier&apos;s bills and
              delivery notes automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
            <FormSection title="Details">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Field
                  id="po-number"
                  label="PO number"
                  required
                  error={showErrors ? fieldErrors.poNumber : null}
                >
                  <Input
                    id="po-number"
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                    placeholder="PO-1001"
                    aria-invalid={showErrors && fieldErrors.poNumber !== null}
                  />
                </Field>

                <Field id="po-supplier" label="Supplier" hint="Can be assigned later.">
                  <Select value={supplierId} onValueChange={setSupplierId}>
                    <SelectTrigger id="po-supplier" className="w-full">
                      <SelectValue placeholder="Select supplier" />
                    </SelectTrigger>
                    <SelectContent>
                      {suppliers.map((s) => (
                        <SelectItem key={s.id} value={s.id!}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field
                  id="po-currency"
                  label="Currency"
                  required
                  error={showErrors ? fieldErrors.currency : null}
                >
                  <Input
                    id="po-currency"
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                    placeholder="GBP"
                    maxLength={3}
                    aria-invalid={showErrors && fieldErrors.currency !== null}
                  />
                </Field>

                <Field id="po-order-date" label="Order date">
                  <Input
                    id="po-order-date"
                    type="date"
                    value={orderDate}
                    onChange={(e) => setOrderDate(e.target.value)}
                  />
                </Field>

                <Field id="po-expected-delivery-date" label="Expected delivery date">
                  <Input
                    id="po-expected-delivery-date"
                    type="date"
                    value={expectedDeliveryDate}
                    onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  />
                </Field>

                <Field
                  id="po-external-ref"
                  label="External reference"
                  hint="Your own id for this order. Echoed back untouched."
                >
                  <Input
                    id="po-external-ref"
                    value={externalRef}
                    onChange={(e) => setExternalRef(e.target.value)}
                    placeholder="Your own reference"
                  />
                </Field>
              </div>
            </FormSection>

            <FormSection
              title="Lines"
              description="SKU is the exact-match key against bill lines. Ext. ref is your own line id, stored and echoed back, never used for matching."
              actions={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setLines((prev) => [...prev, emptyLine()])}
                >
                  <Plus className="size-3.5" />
                  Add line
                </Button>
              }
            >
              <div className="space-y-3">
                {lines.map((line, i) => {
                  const lineError = fieldErrors.lines[i]!;
                  const show = showErrors;
                  const lineTotal = (Number(line.quantityOrdered) || 0) * (Number(line.unitPrice) || 0);
                  return (
                    <LineCard
                      key={i}
                      index={i}
                      invalid={show && hasLineError(lineError)}
                      removeDisabled={lines.length === 1}
                      onRemove={() => setLines((prev) => prev.filter((_, idx) => idx !== i))}
                      summary={
                        lineTotal > 0 ? (
                          <span className="text-sm font-semibold tabular-nums">
                            {formatCurrency(lineTotal, currency || "GBP")}
                          </span>
                        ) : null
                      }
                    >
                      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2.4fr)_minmax(0,1.2fr)_minmax(0,1.2fr)_100px_130px_120px]">
                        <Field
                          id={`po-line-${i}-description`}
                          label="Description"
                          required
                          error={show ? lineError.description : null}
                        >
                          <Input
                            id={`po-line-${i}-description`}
                            placeholder="What you're ordering"
                            value={line.description}
                            onChange={(e) => updateLine(i, { description: e.target.value })}
                            aria-invalid={show && lineError.description !== null}
                          />
                        </Field>
                        <Field id={`po-line-${i}-sku`} label="SKU">
                          <Input
                            id={`po-line-${i}-sku`}
                            placeholder="ABC-123"
                            value={line.sku}
                            onChange={(e) => updateLine(i, { sku: e.target.value })}
                          />
                        </Field>
                        <Field
                          id={`po-line-${i}-external-ref`}
                          label="Ext. ref"
                        >
                          <Input
                            id={`po-line-${i}-external-ref`}
                            placeholder="Your line id"
                            value={line.externalRef}
                            onChange={(e) => updateLine(i, { externalRef: e.target.value })}
                          />
                        </Field>
                        <Field
                          id={`po-line-${i}-quantity`}
                          label="Qty"
                          required
                          error={show ? lineError.quantityOrdered : null}
                        >
                          <Input
                            id={`po-line-${i}-quantity`}
                            type="number"
                            step="any"
                            min="0"
                            placeholder="0"
                            value={line.quantityOrdered}
                            onChange={(e) => updateLine(i, { quantityOrdered: e.target.value })}
                            aria-invalid={show && lineError.quantityOrdered !== null}
                          />
                        </Field>
                        <Field id={`po-line-${i}-unit`} label="Unit">
                          <UnitCombobox
                            placeholder="e.g. each"
                            value={line.unit}
                            onChange={(u) => updateLine(i, { unit: u ?? "" })}
                          />
                        </Field>
                        <Field id={`po-line-${i}-unit-price`} label="Unit price">
                          <Input
                            id={`po-line-${i}-unit-price`}
                            type="number"
                            step="any"
                            min="0"
                            placeholder="0.00"
                            value={line.unitPrice}
                            onChange={(e) => updateLine(i, { unitPrice: e.target.value })}
                          />
                        </Field>
                      </div>
                    </LineCard>
                  );
                })}
              </div>

              {showErrors && fieldErrors.noLines && (
                <p className="text-sm text-destructive">{fieldErrors.noLines}</p>
              )}

              <div className="flex items-baseline justify-end gap-3 border-t pt-3 text-sm">
                <span className="text-muted-foreground">
                  {lines.length} line{lines.length === 1 ? "" : "s"}
                </span>
                <span className="text-muted-foreground">Net total</span>
                <span className="font-semibold tabular-nums">
                  {formatCurrency(netTotal, currency || "GBP")}
                </span>
              </div>
            </FormSection>

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>

          <DialogFooter className="shrink-0 items-center border-t px-6 py-4 sm:justify-between">
            <RequiredLegend />
            <div className="flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting && <Spinner className="size-4" />}
                {isEdit ? "Save changes" : "Create purchase order"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <AlertDialog open={duplicate !== null} onOpenChange={(o) => !o && setDuplicate(null)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>PO {duplicate?.poNumber} already exists</AlertDialogTitle>
          <AlertDialogDescription>
            A purchase order with this number already exists. Replace its lines with the ones
            you just entered? Existing lines already matched to a bill are kept; this can&apos;t
            be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={replacing}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              handleReplace();
            }}
            disabled={replacing}
          >
            {replacing && <Spinner className="size-4" />}
            Replace lines
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
